import { useEffect, useRef } from 'react';

// How long the landing page may sit on its spinner before we consider
// the load "stuck" and emit a funnel event for it (INS-1296). The
// Landing Page pageview fires on mount regardless, so without these
// events a spinner-blocked visitor is indistinguishable from a bounce.
export const LANDING_STUCK_AFTER_MS = 10_000;

type CaptureEvent = (
  name: string,
  properties?: Record<string, unknown>,
) => void;

export function useLandingTelemetry(
  isLoading: boolean,
  hasLoadError: boolean,
  captureEvent: CaptureEvent,
) {
  const mountedAtRef = useRef(Date.now());
  const interactiveFiredRef = useRef(false);
  const failedFiredRef = useRef(false);
  // Ref so the mount-only stuck timer always calls the current capture
  // fn without re-arming when analyticsMeta identity changes.
  const captureRef = useRef(captureEvent);
  captureRef.current = captureEvent;

  // Landing Interactive — the Continue button is actually on screen.
  useEffect(() => {
    if (!isLoading && !interactiveFiredRef.current) {
      interactiveFiredRef.current = true;
      captureRef.current('Landing Interactive', {
        timeToInteractiveMs: Date.now() - mountedAtRef.current,
      });
    }
  }, [isLoading]);

  // Landing Load Failed — the agent/client fetch errored.
  useEffect(() => {
    if (hasLoadError && !failedFiredRef.current) {
      failedFiredRef.current = true;
      captureRef.current('Landing Load Failed', {
        elapsedMs: Date.now() - mountedAtRef.current,
      });
    }
  }, [hasLoadError]);

  // Landing Load Stuck — still on the spinner after the threshold.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!interactiveFiredRef.current) {
        captureRef.current('Landing Load Stuck', {
          durationMs: LANDING_STUCK_AFTER_MS,
        });
      }
    }, LANDING_STUCK_AFTER_MS);
    return () => clearTimeout(timer);
  }, []);
}
